<template>
  <div class="body-components" ref="body-components">
    <resize-observer @notify="resizeBody" />
    <div class="article-body" ref="articleBody">
      <template v-for="(item, index) in contentList">
        <body-text v-if="item.type === 'text'" :item="item" :node="node" :ref="setElementRef('text', index)"></body-text>
        <body-image v-else-if="item.type === 'image'" :item="item" :ref="setElementRef('image', index)" :type="articleType" :shareData="shareData()" :itemIndex="item.itemTypeIndex" :inlinePhotoList="node.inlinePhotoList"></body-image>
        <body-iframe v-else-if="item.type === 'iframe'" :item="item" :ref="setElementRef('iframe', index)"></body-iframe>
        <body-twitter v-else-if="item.type === 'twitter'" :item="item" :ref="setElementRef('twitter', index)"></body-twitter>
        <body-instagram v-else-if="item.type === 'instagram'" :item="item" :ref="setElementRef('instagram', index)"></body-instagram>
        <body-chart v-else-if="item.type === 'chart'" :item="item" :nid="node.nid" :ref="setElementRef('chart', index)"></body-chart>
        <body-blockquote v-else-if="item.type === 'blockquote'" :item="item" :node="node" :ref="setElementRef('blockquote', index)" :isBodyLeftQuoteMode="isBodyLeftQuoteMode"></body-blockquote>

        <div class="related-person-group" v-else-if="item.type === 'person'" :ref="setElementRef('person', index)">
          <body-related-widget class="hidden-sm-down" :item="item" :node="node" theme="desktop"></body-related-widget>
          <body-related-widget class="hidden-sm-up" :item="item" :node="node" theme="mobile"></body-related-widget>
        </div>

        <div class="whatis-widget-group" v-else-if="item.type === 'whatis' && isShowingWhatisWidget(node, item)" :ref="setElementRef('whatis', index)">
          <body-related-widget class="hidden-sm-down" :item="item" :node="node" theme="desktop"></body-related-widget>
          <body-related-widget class="hidden-sm-up" :item="item" :node="node" theme="mobile"></body-related-widget>
        </div>

        <template v-if="getBodyOptions.alsoRead.enable" v-for="(article, articleIndex) in alsoReadArticles">
          <also-read :data-index="index" :article="article" v-if="article.insertAtIndex === index && article.isIndexAvailable"></also-read>
        </template>
        <div v-if="inlineNewsletterIndex && inlineNewsletterIndex === index" class="center-wrapper hidden-md-up">
          <div class="newsletter-form-wrapper no-article-gutter-mobile" v-if="getBodyOptions.inlineNewsletter">
            <inline-newsletter-form :newsletterType="getNewsLetterType" :theme="newsLetterTheme"></inline-newsletter-form>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
<script>
  import { ResizeObserver } from 'vue-resize'
  import EventBus from 'components/helpers/event-bus'

  import get from 'lodash/get'
  import find from 'lodash/find'
  import assignIn from 'lodash/assignIn'
  import forEach from 'lodash/forEach'
  import reduce from 'lodash/reduce'
  import includes from 'lodash/includes'
  import filter from 'lodash/filter'
  import map from 'lodash/map'
  import head from 'lodash/head'
  import isEmpty from 'lodash/isEmpty'

  import AlsoRead from 'components/node/AlsoRead.vue'
  import photoSlider from 'components/mixins/photoSlider'

  import overlappingQuotes from 'components/mixins/overlappingQuotes'
  import InlineNewsletterForm from 'components/widgets/InlineNewsletterForm.vue'

  import { getNewsLetterMapping, getArticleType, isDesktop } from 'mixins/utilities'

  import BodyText from 'components/node/fields/body-items/Text.vue'
  import BodyImage from 'components/node/fields/body-items/Image.vue'

  export default {
    name: 'field-body',
    components: {
      AlsoRead,
      InlineNewsletterForm,
      BodyText,
      BodyImage,
      BodyIframe: () => import('components/node/fields/body-items/Iframe.vue'),
      BodyTwitter: () => import('components/node/fields/body-items/Twitter.vue'),
      BodyInstagram: () => import('components/node/fields/body-items/Instagram.vue'),
      BodyChart: () => import('components/node/fields/body-items/Chart.vue'),
      BodyBlockquote: () => import('components/node/fields/body-items/Blockquote.vue'),
      BodyRelatedWidget: () => import('components/node/fields/body-items/RelatedPerson.vue'),
      ResizeObserver,
    },
    props: {
      body: [String],
      bodyOptions: Object,
      node: Object,
    },
    data() {
      return {
        isDesktop: false,
      }
    },
    mixins: [photoSlider, overlappingQuotes],
    computed: {
      getNewsLetterType() {
        return getNewsLetterMapping(get(this, 'node.sections', {}))
      },
      articleType() {
        return getArticleType(get(this, 'node', {}))
      },
      newsLetterTheme() {
        switch (getArticleType(get(this, 'node', {}))) {
          case 'countdown':
            return 'white'
          case 'live':
            return 'white'
          case 'opinion':
            return 'white'
          case 'photo-essay':
            return 'black'
          case 'review':
            return 'white'
          case 'video':
            return 'black'
          default:
            return 'white'
        }
      },
      getBodyOptions() {
        return assignIn({
          countDown: false,
          photoOverlay: false,
          inlineNewsletter: true,
          hideParagraphArrow: false,
          alsoRead: {
            enable: true,
            alsoReadLayout: [4, 3, 4, 4, 4],
            insertPosition: [6, 9, 12, 15, 18],
          },
        }, this.bodyOptions)
      },
      filterBody() {
        return this.node.body
      },
      contentList() {
        let counter = {}
        forEach(get(this, 'node.contentList', []), (value) => {
          counter = assignIn(counter, {[`${value.type}`]: get(counter, `[${value.type}]`, 0) + 1})
          value.itemTypeIndex = counter[`${value.type}`]
        })
        return get(this, 'node.contentList', [])
      },
      relatedArticles() {
        return get(this, 'node.related_articles', [])
      },
      isBodyLeftQuoteMode() {
        switch (get(this, 'node.type', {})) {
          case 'article': {
            const exemptedArticleTypes = ['photo-essay', 'video']
            return !(includes(exemptedArticleTypes, getArticleType(get(this, 'node', {}))))
          }
          case 'comparison':
            return false
          default:
            return false
        }
      },
      inlineNewsletterIndex() {
        const contentListLength = this.contentList.length || 0
        const firstAlsoReadPosition = get(this, 'getBodyOptions.alsoRead.insertPosition[0]', false)
        let index = false
        if (contentListLength) {
          // No Also Read
          if (isEmpty(this.alsoReadArticles)) {
            // Newsletter widget goes at first also read position
            if (firstAlsoReadPosition) {
              // If first position > content list length
              index = contentListLength >= firstAlsoReadPosition ? firstAlsoReadPosition - 1 : contentListLength - 1
            }
          } else if (this.alsoReadArticles.length === 1) {
            const lastAlsoReadPosition = get(this, 'getBodyOptions.alsoRead.insertPosition[0]', false)
            // If larger than certain number of paragraph
            if (lastAlsoReadPosition && contentListLength >= (lastAlsoReadPosition + 4)) {
              // Place it in the 2nd last paragraph
              index = contentListLength - 2 - 1
            }
          }
        }

        // To avoid H Tag
        while (get(this, `contentList[${index}].content.html`) && this.contentList[index].content.html.match(/<h[1-5]>(.*)?<\/h[1-5]>/)) index -= 1
        return index
      },
      alsoReadArticlesIndexesAvailable() {
        const bodyOptionPositionsArray = get(this, 'getBodyOptions.alsoRead.insertPosition', [])

        // separate as PASS and FAIL to see which ones to place at the bottom(fail) or insert into the content(pass)
        const [pass, fail] = reduce(bodyOptionPositionsArray, (result, position) => {
          result[(position - 1 <= this.contentList.length) ? 0 : 1].push(position - 1) // less 1, since using Index to match to content list
          return result
        }, [[], []])

        const indexesAvailableForAlsoRead = {pass, fail}
        return indexesAvailableForAlsoRead
      },
      alsoReadArticles() {
        const isEnabled = get(this, 'getBodyOptions.alsoRead.enable', false)
        const alsoReadArticles = []

        if (isEnabled) {
          const bodyOptionPositionsArray = get(this, 'getBodyOptions.alsoRead.insertPosition', [])
          const bodyOptionsLayoutsArray = get(this, 'getBodyOptions.alsoRead.alsoReadLayout', [])
          const indexesAvailablePass = get(this, 'alsoReadArticlesIndexesAvailable[pass]', [])

          // Go through related articles and set additional details for the Also Read articles
          forEach(this.relatedArticles, (article, articleIndex) => {
            let getLayoutType = 4
            let getInsertAtIndex = this.contentList.length
            let getIsIndexAvailable = false

            // Assign corresponding layout type to the matching index in the Layouts array
            forEach(bodyOptionsLayoutsArray, (type, typeIndex) => {
              if (articleIndex === typeIndex) {
                getLayoutType = type
                return false
              }
              return true
            })

            // Assign the position(converted as index) to insert in Contentlist: Match the corresponding article index to the matching index in the Positions array and assign it the Position (converted as index)
            forEach(bodyOptionPositionsArray, (position, positionIndex) => {
              if (articleIndex === positionIndex) {
                getInsertAtIndex = position - 1 // less 1, since using Index to match to content list
                return false
              }
              return true
            })

            // Check if the index that will be inserted in is part of the PASS index availables
            forEach(indexesAvailablePass, (Available) => {
              if (getInsertAtIndex === Available) {
                getIsIndexAvailable = true
                return false
              }
              return true
            })

            // To avoid H Tag
            while (get(this, `contentList[${getInsertAtIndex}].content.html`) && this.contentList[getInsertAtIndex].content.html.match(/<h[1-5]>(.*)?<\/h[1-5]>/)) getInsertAtIndex -= 1

            const alsoReadDetails = {
              node: article,
              layoutType: getLayoutType,
              insertAtIndex: getInsertAtIndex,
              isIndexAvailable: getIsIndexAvailable,
              isLast: articleIndex === this.relatedArticles.length - 1,
            }

            alsoReadArticles.push(alsoReadDetails)
          })
        }
        return alsoReadArticles
      },
    },
    mounted() {
      this.isDesktop = isDesktop()

      this.arrowPlacement()
    },
    updated() {
      this.repositionOverlappingQuotes()
    },
    methods: {
      isShowingWhatisWidget(node = {}, item) {
        const whatisNid = get(item, 'content.attr.data-whatis-id', false)
        const whatis = find(get(node, 'what_is_articles', []), ['nid', whatisNid]) || {}
        return get(whatis, 'short_title')
      },
      shareData() {
        const obj = {
          url: get(this.node, 'url', ''),
          shortenUrl: get(this.node, 'shorten_url', ''),
          title: get(this.node, 'title', ''),
        }
        return obj
      },
      setElementRef(key, index) {
        // elementRef-${index}: same name as in node.js for handleBlockquote()
        return `processed-${key}-${index}`
      },
      arrowPlacement() {
        if (!this.getBodyOptions.hideParagraphArrow) {
          forEach(get(this, 'contentList', []), (item, index) => {
            // Keep original reference for first non-image element
            if (index === 0 && get(item, 'type', '') !== 'image') {
              this.addClassToBodyRef(item.type, index, 'arrow-placement')
              return false
            }

            // If an image, use closest non-image element reference
            if (get(item, 'type', '') === 'image') {
              const nextIndex = index + 1
              const nextItem = get(this, `contentList[${nextIndex}]`, false)

              if (nextItem && get(nextItem, 'type', '') !== 'image') {
                this.addClassToBodyRef(nextItem.type, nextIndex, 'arrow-placement')
                return false
              }
            }
            return true
          })
        }
      },
      addClassToBodyRef(key, index, className) {
        const referenceElement = get(this, `$refs[processed-${key}-${index}]`, false)

        if (referenceElement) {
          const elementClassList = get(head(referenceElement), '$el.classList', false)

          if (elementClassList) {
            elementClassList.add(className)
          }
        }
      },
      resizeBody() {
        const bq = map(filter(this.$refs, (element, key) => includes(key, 'processed-blockquote-')), '[0].$el')
        this.isDesktop = isDesktop()
        if (isDesktop()) {
          if (bq.length !== 0 && this.isBodyLeftQuoteMode) {
            EventBus.$emit('repositionQuoteToRelatedEl')
            this.$nextTick(function () {
              this.repositionOverlappingQuotes()
            })
          } else {
            EventBus.$off('repositionQuoteToRelatedEl')
          }
        }
      },
    },
  }
</script>

<style lang="scss">
  .newsletter-form-wrapper {
    @include respond-to('mobile') {
      margin-bottom: rem-calc(28.28);
    }
    @include respond-to('desktop') {
      width: rem-calc(540);
      margin-bottom: rem-calc(40);
    }
  }
  .article-body {
    word-wrap: break-word;
    position: relative;
    @include respond-to('desktop') {
      margin-bottom: rem-calc(40);
      font-size: rem-calc(20);
      line-height: rem-calc(30);
    }
    @include respond-to('mobile') {
      margin-bottom: rem-calc(28);
    }
    div[class$="-wrapper"] {
      margin-bottom: rem-calc(28);
      text-align: center;
    }
    iframe {
      @include respond-to('desktop') {
        margin-top: rem-calc(0);
        margin-left: auto;
        margin-right: auto;
        margin-bottom: rem-calc(32);
      }
      @include respond-to('mobile') {
        // To override inline style
        margin-top: rem-calc(0);
        margin-left: auto;
        margin-right: auto;
        margin-bottom: rem-calc(28);
      }
    }
    iframe, img {
      max-width: 100%;
    }
    img {
      height: auto;
    }

    .also-read-wrapper .also-read-article-wrapper &:nth-child(n+2) .also-read-tag {
      display: none;
    }

    .also-read-article-wrapper {
      text-align: left;
      @include respond-to('desktop') {
        margin: auto;
        max-width: rem-calc(480);
        .article-level-four .card-title a {
          font-size: rem-calc(24);
          line-height: rem-calc(34);
        }
      }
    }

    .img-caption-processed {
      @include respond-to('mobile'){
        margin-left: rem-calc(-$mobile-article-gutter);
        margin-right: rem-calc(-$mobile-article-gutter);
        figcaption{
          padding-left: rem-calc($mobile-article-gutter);
          padding-right: rem-calc($mobile-article-gutter);
        }
      }
      margin-left: rem-calc(-$desktop-article-gutter);
      margin-right: rem-calc(-$desktop-article-gutter);
    }


    .related-person-wrapper {
      @include respond-to('desktop') {
        margin-left: rem-calc(-50);
        margin-right: rem-calc(-50);
      }
      @include respond-to('mobile') {
        margin-left: rem-calc(-$mobile-article-gutter);
        margin-right: rem-calc(-$mobile-article-gutter);
      }
    }

    .no-link{
      pointer-events:none;
    }
  }

  .body-components{
    position: relative;
  }
</style>
<style lang="scss" scoped>
  .arrow-right-wrapper{
    position: absolute;
    left: 0;
    height: rem-calc(25);
    width: rem-calc(15);
    svg {
      fill: $rose;
      height: 100%;
      width: 100%;
    }
    @include respond-to('mobile') {
      margin-left: - rem-calc($mobile-article-gutter);
    }
    @include respond-to('desktop') {
      margin-top: rem-calc(4);
      margin-left: - rem-calc(54);
    }
  }

  @include respond-to('desktop') {
    .center-wrapper{
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }

  .arrow-placement {
    &::before {
      content: "";
      display: block;
      height: rem-calc(25);
      width: rem-calc(15);

      background-image: url('/static/svg/raw/icon_arrow_right1.svg');
      background-size: contain;

      position: absolute;
      left: rem-calc(-54);
      margin-top: rem-calc(4);
    }
  }
</style>



// WEBPACK FOOTER //
// Body.vue?31b22e58